import type { ProfileResponse } from '@shared/schemas';
import type {
  LicenceProfileTypesEdit,
  ProfileTypeAddition,
  ProfileTypeCategory,
} from '@shared/schemas/profile-type-blocks';

import { typeDisplayName } from '@/shared/profile/type-display';
import {
  type ExperienceGroup,
  type ExperienceItem,
  groupExperiencesByCompany,
} from '@/utils/format';

/**
 * Pure derivations for the Profile Detail page. Everything the original component
 * computed inline (the LinkedIn-style experience grouping, the current role, data
 * freshness) lives here as framework-free functions, so each rule is
 * unit-testable and the selectors/views just call them. The grouping delegates to
 * the shared `@/utils/format` primitives. The Overview's rules live next door, in
 * `profile-overview.rules.ts`.
 */

/** Enrichment is normally skipped for data younger than this many days. */
export const STALE_DAYS = 30;

const MS_PER_DAY = 86_400_000;

/** Experiences grouped by company (LinkedIn-style), newest first. */
export function groupedExperiences(profile: ProfileResponse | null): ExperienceGroup[] {
  if (!profile) return [];
  return groupExperiencesByCompany((profile.experiences ?? []) as ExperienceItem[]);
}

/** The current (ongoing) experience, if any. */
export function currentExperience(profile: ProfileResponse | null) {
  return profile?.experiences?.find((e) => e.is_current) ?? null;
}

/** The company id of the current role (drives the header company link). */
export function currentCompanyId(profile: ProfileResponse | null): string | null {
  return currentExperience(profile)?.company_id ?? null;
}

/** Age of the profile data in whole days at `nowMs`, or null if never updated. */
export function dataAgeInDays(profile: ProfileResponse | null, nowMs: number): number | null {
  if (!profile?.data_updated_at) return null;
  return Math.floor((nowMs - new Date(profile.data_updated_at).getTime()) / MS_PER_DAY);
}

/** Data is "fresh" (enrichment would normally be skipped) when younger than STALE_DAYS. */
export function isFreshData(ageDays: number | null): boolean {
  return ageDays !== null && ageDays < STALE_DAYS;
}

/**
 * Why enrichment is unavailable for this profile, or null when it is available.
 *
 * A deactivated profile is out of scope for every list, export and enrichment
 * run, and the server refuses it (`rule_violated`). This page is the one place
 * that could still ask: a list view deliberately keeps showing its deactivated
 * members, so the button sits right next to them.
 *
 * Returns the sentence rather than a boolean because the button is disabled with
 * the reason in a tooltip, not hidden; an admin learns the rule instead of
 * watching an action disappear with no way to know when it comes back (PI-93).
 */
export function enrichBlockedReason(profile: ProfileResponse | null): string | null {
  if (!profile?.deactivated_at) return null;
  return 'This profile is deactivated. Restore it to enrich it again.';
}

/** Each whitelisted type's main category, as filed on the profile types page (#629). */
export type TypeCategories = Record<string, ProfileTypeCategory | null>;

/**
 * The main category the admin's "Types" dialog names before each type; "HNWI ·
 * Family Office", "Institutionals · Pension Fund" (#629). The chips under the
 * profile's name show the type alone since #672. Null when there is no type, the
 * categories are not in (a client, or a failed load), or nobody filed the type yet:
 * the chip then reads as the type alone.
 */
export function typeCategory(
  type: string | null | undefined,
  categories: TypeCategories | null,
): ProfileTypeCategory | null {
  if (!type || !categories) return null;
  return categories[type] ?? null;
}

/**
 * The types an admin can add by hand in the "Types" dialog (#701): the whitelist,
 * in the priority order of the Profile types page (the order `GET /profile-types`
 * answers, which the categories keep: string keys keep their insertion order), less
 * the types the profile carries and those removed by hand, which Restore puts back.
 */
export function addableTypes(
  categories: TypeCategories | null,
  edit: { types: string[]; removed: { type: string }[] } | null,
): string[] {
  if (!categories || !edit) return [];
  const taken = new Set([...edit.types, ...edit.removed.map((r) => r.type)]);
  return Object.keys(categories).filter((type) => !taken.has(type));
}

/**
 * One line of a client's "Types" dialog (#723): a type as the chips show it, by
 * its display name (FO and SFO are one Family Office), the stored labels behind
 * it, and whether the licence may remove it: when it added at least one of them.
 * Remove takes off only the licence's labels; one Invyo set stays, and so does
 * the line. Otherwise a licence's SFO under Invyo's FO could never be taken back,
 * though it still counts (and may still be the primary type).
 */
export interface LicenceTypeRow {
  name: string;
  /** The first stored label, the one the chip is drawn from. */
  label: string;
  /** The labels the licence added behind this name: what Remove takes off. */
  ownLabels: string[];
  removable: boolean;
  /** Whether Invyo set a label behind this name: it stays after a Remove. */
  setByInvyo: boolean;
  /** Who in the licence added it and when, for a removable line. */
  addition: ProfileTypeAddition | null;
}

export function licenceTypeRows(edit: LicenceProfileTypesEdit | null): LicenceTypeRow[] {
  if (!edit) return [];
  const rows = new Map<string, LicenceTypeRow>();
  for (const label of edit.types) {
    const name = typeDisplayName(label);
    const addition = edit.added.find((a) => a.type === label) ?? null;
    const row = rows.get(name) ?? { name, label, ownLabels: [], removable: false, setByInvyo: false, addition: null };
    if (addition) {
      row.ownLabels.push(label);
      row.removable = true;
      row.addition ??= addition;
    } else {
      row.setByInvyo = true;
    }
    rows.set(name, row);
  }
  return [...rows.values()];
}

/**
 * The types a client can add for their licence (#723): one per display name, in
 * the priority order of the Profile types page, the name standing for its
 * highest-priority label; never a name the profile already shows. The stored
 * labels (FO, SFO) are Invyo's vocabulary, so a client picks the name.
 */
export function licenceAddableTypes(
  categories: TypeCategories | null,
  edit: Pick<LicenceProfileTypesEdit, 'types'> | null,
): Array<{ label: string; name: string }> {
  if (!categories || !edit) return [];
  const shown = new Set(edit.types.map(typeDisplayName));
  const options = new Map<string, string>();
  for (const label of Object.keys(categories)) {
    const name = typeDisplayName(label);
    if (!shown.has(name) && !options.has(name)) options.set(name, label);
  }
  return [...options.entries()].map(([name, label]) => ({ label, name }));
}
